import type { EquipmentCategory, EquipmentItem } from "@infoflyt/shared/kameraplan";
import { supabase } from "./supabase";

/**
 * Produksjonens utstyrskatalog.
 *
 * PLANEN PEKER HIT og lagrer aldri navnet selv. Får et utstyr nytt navn,
 * får det nytt navn i alle planer som bruker det — og i huskelista.
 *
 * ALLE TEKNISKE FELT ER VALGFRIE. Mangler de, tegnes ingen kjegle. Vi gjetter
 * aldri på en sensorbredde eller en strålevinkel; en kjegle som er tegnet
 * feil ser like riktig ut som en som stemmer.
 *
 * Kaster ikke. Uten klient eller uten nett er katalogen tom, og det er en
 * tilstand skjermen må kunne vise.
 */

const FELT = "id, production_id, category, name, quantity_available, sensor_width_mm,"
  + " focal_length, zoom_min, zoom_max, power_w, color_temp_min, color_temp_max,"
  + " beam_min, beam_max, note, archived, sort_order";

/** Feltene som kan skrives. `id` og `production_id` hører til raden. */
export interface Skriv {
  readonly category: EquipmentCategory;
  readonly name: string;
  readonly quantityAvailable?: number | null;
  readonly sensorWidthMm?: number | null;
  readonly focalLength?: number | null;
  readonly zoomMin?: number | null;
  readonly zoomMax?: number | null;
  readonly powerW?: number | null;
  readonly colorTempMin?: number | null;
  readonly colorTempMax?: number | null;
  readonly beamMin?: number | null;
  readonly beamMax?: number | null;
  readonly note?: string;
}

const t = (v: unknown): number | undefined =>
  v === null || v === undefined ? undefined : Number(v);

function tilVare(r: Record<string, unknown>): EquipmentItem {
  const zMin = t(r["zoom_min"]);
  const zMaks = t(r["zoom_max"]);
  const fMin = t(r["color_temp_min"]);
  const fMaks = t(r["color_temp_max"]);
  const sMin = t(r["beam_min"]);
  const sMaks = t(r["beam_max"]);

  return {
    id: String(r["id"]),
    productionId: String(r["production_id"]),
    category: String(r["category"]) as EquipmentCategory,
    name: String(r["name"]),
    ...(t(r["quantity_available"]) === undefined
      ? {} : { quantityAvailable: t(r["quantity_available"])! }),
    ...(t(r["sensor_width_mm"]) === undefined
      ? {} : { sensorWidthMm: t(r["sensor_width_mm"])! }),
    ...(t(r["focal_length"]) === undefined ? {} : { focalLength: t(r["focal_length"])! }),
    ...(zMin !== undefined && zMaks !== undefined ? { zoomRange: [zMin, zMaks] as const } : {}),
    ...(t(r["power_w"]) === undefined ? {} : { powerW: t(r["power_w"])! }),
    /* Ett tall når det ikke er et område. En lampe med fast farge skal ikke
       vises som «5600–5600». */
    ...(fMin === undefined ? {}
      : { colorTempK: fMaks === undefined || fMaks === fMin ? fMin : [fMin, fMaks] as const }),
    ...(sMin === undefined ? {}
      : { beamAngleDeg: sMaks === undefined || sMaks === sMin ? sMin : [sMin, sMaks] as const }),
    note: String(r["note"] ?? ""),
    archived: r["archived"] === true,
  };
}

function tilRad(s: Skriv): Record<string, unknown> {
  return {
    category: s.category,
    name: s.name.trim(),
    quantity_available: s.quantityAvailable ?? null,
    sensor_width_mm: s.sensorWidthMm ?? null,
    focal_length: s.focalLength ?? null,
    zoom_min: s.zoomMin ?? null,
    zoom_max: s.zoomMax ?? null,
    power_w: s.powerW ?? null,
    color_temp_min: s.colorTempMin ?? null,
    color_temp_max: s.colorTempMax ?? null,
    beam_min: s.beamMin ?? null,
    beam_max: s.beamMax ?? null,
    note: s.note ?? "",
  };
}

export async function hent(produksjonId: string): Promise<EquipmentItem[]> {
  if (supabase === null) return [];
  const { data, error } = await supabase
    .from("equipment")
    .select(FELT)
    .eq("production_id", produksjonId)
    .order("category", { ascending: true })
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (error !== null) return [];
  return (data ?? []).map((r) => tilVare(r as unknown as Record<string, unknown>));
}

export async function leggTil(
  produksjonId: string, felt: Skriv,
): Promise<EquipmentItem | null> {
  if (supabase === null || felt.name.trim() === "") return null;
  const { data, error } = await supabase
    .from("equipment")
    .insert({ production_id: produksjonId, ...tilRad(felt) })
    .select(FELT)
    .single();
  if (error !== null || data === null) return null;
  return tilVare(data as unknown as Record<string, unknown>);
}

export async function endre(id: string, felt: Skriv): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase.from("equipment").update(tilRad(felt)).eq("id", id);
  return error === null;
}

/**
 * Arkiverer — eller henter fram igjen.
 *
 * UTSTYR SOM ER I BRUK SLETTES IKKE. En plan som peker på det skal fortsatt
 * kunne tegnes, og huskelista skal fortsatt kunne si hva som må pakkes.
 */
export async function arkiver(id: string, arkivert: boolean): Promise<boolean> {
  if (supabase === null) return false;
  const { error } = await supabase.from("equipment").update({ archived: arkivert }).eq("id", id);
  return error === null;
}
